"use client";

import { useEffect, useState } from "react";
import Popup from "reactjs-popup";
import Link from "next/link";
import { useAuth0 } from "@auth0/auth0-react";
import { handlePayment } from "@/src/helpers/stripe";
import { captureEvent } from "@/src/helpers/trackEvent";
import { PRO_BENEFITS } from "@/src/lib/pricing";
import { useRouter } from "next/navigation";

const QuarkleImage = "/images/quarkle.png";


export default function UpgradePopup({ open, closeModal, page }) {
  const { user, isAuthenticated, loginWithPopup, getAccessTokenSilently, logout } = useAuth0();
  const [initiatePayment, setInitiatePayment] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isYearly, setIsYearly] = useState(true);
  const router = useRouter();

  function redirectToStripe() {
    const plan = isYearly ? "pro-yearly" : "pro";
    return handlePayment(plan, user, router, setError, setIsLoading, getAccessTokenSilently, logout);
  }

  useEffect(() => {
    if (open) captureEvent("upgrade_plan_viewed", { surface: page || "editor" });
  }, [open, page]);

  useEffect(() => {
    if (isAuthenticated && initiatePayment)
      redirectToStripe().then(() => {
        setInitiatePayment(false);
      });
  }, [isAuthenticated, initiatePayment]);

  async function handleProPayment() {
    captureEvent(page === "onboarding" ? "onboarding_upgrade_popup_payment_clicked" : "upgrade_popup_payment_clicked", {
      plan: isYearly ? "pro-yearly" : "pro",
    });
    if (!isAuthenticated) {
      try {
        await loginWithPopup();
      } catch (error) {
        setError(error);
      }
    }
    setInitiatePayment(true);
  }

  const originalPrice = isYearly ? 240 : 19.99;
  const yearlyPrice = 120;
  const monthlyPrice = 4.99;
  const currentPrice = isYearly ? yearlyPrice : monthlyPrice;
  const billingPeriod = isYearly ? "year" : "month";

  return (
    <Popup
      open={open}
      closeOnDocumentClick
      onClose={closeModal}
      overlayStyle={{ background: "var(--qk-scrim)", backdropFilter: "blur(3px)" }}
      contentStyle={{ background: "transparent", border: "none", padding: 0, width: "auto" }}
    >
      <div
        className="qk-editor qk-paper-bg relative"
        style={{
          width: "min(92vw, 520px)",
          maxHeight: "92vh",
          overflowY: "auto",
          border: "1.5px solid var(--qk-ink)",
          boxShadow: "8px 8px 0 var(--qk-pink-deep)",
          borderRadius: 14,
          color: "var(--qk-ink)",
        }}
      >
        {/* tape-sticker identity ribbon */}
        <div
          className="qk-caps absolute z-10"
          style={{
            top: 18,
            left: -8,
            padding: "5px 14px",
            background: "var(--qk-lime)",
            // Text on a lime fill is fixed in every theme (--qk-on-lime).
            color: "var(--qk-on-lime)",
            border: "1.5px solid var(--qk-on-lime)",
            transform: "rotate(-3deg)",
            boxShadow: "2px 2px 0 var(--qk-on-lime)",
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          for writers who mean it
        </div>

        <button
          type="button"
          aria-label="close"
          className="absolute z-10 flex h-8 w-8 items-center justify-center rounded-full"
          style={{
            top: 14,
            right: 14,
            border: "1px solid var(--qk-rule)",
            background: "var(--qk-paper-3)",
            color: "var(--qk-ink)",
          }}
          onClick={closeModal}
        >
          <i className="pi pi-times text-[11px]"></i>
        </button>

        <div className="px-8 pt-14 pb-6">
          {/* label + mascot */}
          <div className="flex items-center justify-between">
            <span className="qk-caps" style={{ color: "var(--qk-pink-deep)" }}>
              quarkle pro · the full room
            </span>
            <img
              src={QuarkleImage}
              alt=""
              className="h-10 w-10 rounded-full"
              style={{ border: "1.5px solid var(--qk-ink)" }}
            />
          </div>

          {/* headline */}
          <h2
            className="qk-tanker mt-4"
            style={{
              fontSize: "clamp(34px, 5.4vw, 46px)",
              lineHeight: 0.95,
              letterSpacing: "-0.03em",
              margin: 0,
            }}
          >
            the room,{" "}
            <span className="qk-display" style={{ fontStyle: "italic", color: "var(--qk-pink-deep)" }}>
              unlocked.
            </span>
          </h2>

          {/* subhead */}
          <p
            className="qk-display mt-3"
            style={{
              fontSize: 16,
              fontStyle: "italic",
              opacity: 0.84,
              lineHeight: 1.55,
              color: "var(--qk-ink)",
              margin: 0,
            }}
          >
            free already writes with you. here&rsquo;s what pro adds.
          </p>

          {/* dashed rule */}
          <div className="qk-dash-rule mt-5" />

          {/* benefits */}
          <ul className="mt-4 space-y-2.5" style={{ fontFamily: "'Satoshi', system-ui, sans-serif", fontSize: 14, margin: 0, padding: 0, listStyle: "none" }}>
            {PRO_BENEFITS.map((b) => (
              <li key={b.emp} className="flex items-baseline gap-2.5">
                <span style={{ color: "var(--qk-moss)", fontSize: 12, flexShrink: 0, lineHeight: 1 }}>✦</span>
                <span>
                  <span style={{ fontWeight: 600, color: "var(--qk-ink)" }}>{b.emp}</span>{" "}
                  <span style={{ color: "var(--qk-ink)", opacity: 0.78 }}>{b.rest}</span>
                </span>
              </li>
            ))}
          </ul>

          {/* pricing block */}
          <div
            className="mt-5 relative"
            style={{
              background: "var(--qk-paper-3)",
              border: "1px solid var(--qk-rule)",
              borderRadius: 12,
              padding: "14px 16px",
            }}
          >
            {/* yearly ribbon */}
            {isYearly && (
              <div
                className="qk-hand absolute"
                style={{
                  top: -12,
                  right: 14,
                  background: "var(--qk-pink-deep)",
                  color: "var(--qk-on-pink)",
                  padding: "2px 10px",
                  borderRadius: 999,
                  fontSize: 14,
                  lineHeight: 1.2,
                  transform: "rotate(2deg)",
                  border: "1.5px solid var(--qk-ink)",
                  boxShadow: "2px 2px 0 var(--qk-ink)",
                }}
              >
                most writers pick this
              </div>
            )}

            {/* billing toggle (segmented, matches /pro) */}
            <div
              className="inline-flex"
              style={{
                gap: 2,
                padding: 3,
                background: "var(--qk-paper)",
                border: "1px solid var(--qk-rule)",
                borderRadius: 999,
              }}
            >
              {[
                { k: false, label: "monthly" },
                { k: true, label: "yearly" },
              ].map((o) => {
                const on = isYearly === o.k;
                return (
                  <button
                    key={o.label}
                    type="button"
                    onClick={() => setIsYearly(o.k)}
                    className="qk-mono"
                    style={{
                      padding: "5px 14px",
                      borderRadius: 999,
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.14em",
                      color: on ? "var(--qk-on-pink)" : "var(--qk-ink)",
                      background: on ? "var(--qk-pink-deep)" : "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: on ? 600 : 500,
                      transition: "all 0.18s ease",
                    }}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>

            {/* price */}
            <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <del className="qk-display" style={{ fontSize: 18, color: "var(--qk-muted)" }}>

                ${originalPrice}
              </del>
              <span className="qk-tanker" style={{ fontSize: 36, lineHeight: 0.9, color: "var(--qk-ink)" }}>
                ${currentPrice}
              </span>
              <span className="qk-caps" style={{ color: "var(--qk-muted)" }}>
                / {billingPeriod}
              </span>
              {!isYearly && (
                <span className="qk-mono" style={{ fontSize: 15, fontWeight: 600, width: "100%", color: "var(--qk-ink)" }}>
                  first month · then $19.99/month
                </span>
              )}
              <span
                className="qk-caps"
                style={{
                  marginLeft: "auto",
                  color: "var(--qk-pink-deep)",
                  background: "var(--qk-pink-soft)",
                  padding: "3px 8px",
                  borderRadius: 999,
                  fontSize: 10,
                }}
              >
                {isYearly ? "save 50%" : "75% off · first month"}
              </span>
            </div>
          </div>

          {/* error */}
          {error && (
            <p className="qk-hand mt-3" style={{ color: "var(--qk-clay)", fontSize: 15 }}>
              {error.message}
            </p>
          )}

          {/* CTAs */}
          <div className="mt-5 flex items-center gap-2">
            <button type="button" className="qk-pill" onClick={closeModal}>
              maybe later
            </button>
            <button
              type="button"
              onClick={handleProPayment}
              disabled={isLoading}
              className="qk-pill flex-1 justify-center"
              style={{
                background: "var(--qk-pink-deep)",
                color: "var(--qk-on-pink)",
                borderColor: "var(--qk-ink)",
                padding: "10px 16px",
                fontSize: 12,
                boxShadow: "3px 3px 0 var(--qk-ink)",
                cursor: isLoading ? "wait" : "pointer",
              }}
            >
              {isLoading ? (
                <span className="pi pi-spinner pi-spin"></span>
              ) : (
                <>start pro →</>
              )}
            </button>
          </div>

          {/* footer */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <span
              className="qk-mono"
              style={{
                fontSize: 10,
                opacity: 0.62,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--qk-ink)",
              }}
            >
              cancel in a click
            </span>
            <Link
              href="/pro"
              className="qk-hand underline"
              style={{ fontSize: 16, color: "var(--qk-pink-deep)" }}
            >
              see what pro unlocks →
            </Link>
          </div>
        </div>
      </div>
    </Popup>
  );
}
